// # Filename: src/components/poker/shared/PokerTableChat.jsx
// Desktop-only table chat. Self-gates on viewport width (rather than trusting whatever
// tier mounted it) since that's cheap insurance and matches how it always worked.

import React, { useCallback, useEffect, useRef, useState } from "react";

import Button from "../../ui/Button";
import { showToast } from "../../../utils/toast/Toast";
import { ensureFreshAccessToken } from "../../../auth/ensureFreshAccessToken";
import { getChatWSUrl } from "../../../websocket/getWebsocketURL";
import useMediaQuery from "./useMediaQuery";

const DESKTOP_TABLE_MEDIA_QUERY = "(min-width: 768px)";

export default function PokerTableChat({ gameId }) {
  const isDesktop = useMediaQuery(DESKTOP_TABLE_MEDIA_QUERY);
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const wsRef = useRef(null);
  const messagesRef = useRef(null);

  useEffect(() => {
    if (!gameId || !isDesktop) return undefined;
    let closed = false;
    let socket = null;

    const connect = async () => {
      const token = await ensureFreshAccessToken({ minTtlSeconds: 60 });
      if (!token || closed) return;
      socket = new WebSocket(getChatWSUrl({ gameType: "poker_table", lobbyId: gameId, token }));
      wsRef.current = socket;
      socket.onopen = () => {
        if (!closed) setConnected(true);
      };
      socket.onclose = () => {
        if (!closed) setConnected(false);
      };
      socket.onerror = () => {
        if (!closed) setConnected(false);
      };
      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type !== "chat_message" || !data.message?.content) return;
          setMessages((current) => {
            const exists = data.message.id && current.some((message) => message.id === data.message.id);
            return exists ? current : [...current.slice(-29), data.message];
          });
        } catch {
          // Ignore malformed chat messages.
        }
      };
    };

    connect();
    return () => {
      closed = true;
      setConnected(false);
      if (socket && socket.readyState === WebSocket.OPEN) socket.close();
    };
  }, [gameId, isDesktop]);

  useEffect(() => {
    const node = messagesRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  const sendMessage = useCallback(() => {
    const text = draft.trim();
    if (!text) return;
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      showToast("error", "Table chat is still connecting.");
      return;
    }
    wsRef.current.send(JSON.stringify({ type: "chat_message", message: text }));
    setDraft("");
  }, [draft]);

  if (!isDesktop) return null;

  return (
    <aside
      className="relative hidden w-[clamp(220px,23.4vw,300px)] shrink-0 self-stretch overflow-hidden rounded-xl border border-white/[0.08] bg-[linear-gradient(180deg,rgba(20,20,22,0.92),rgba(8,8,9,0.97))] shadow-[0_18px_46px_rgba(0,0,0,0.5)] md:flex md:flex-col"
    >
      <div className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-white/[0.12] to-transparent" />
      <div className="flex items-center justify-between border-b border-white/[0.06] px-3 py-2">
        <span className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-50/80">Table Chat</span>
        <span className={["inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.16em]", connected ? "text-emerald-200" : "text-text-faint"].join(" ")}>
          <span className={["h-1.5 w-1.5 rounded-full", connected ? "bg-emerald-300 shadow-[0_0_6px_rgba(110,231,183,0.85)]" : "bg-text-faint/50"].join(" ")} />
          {connected ? "Live" : "Connecting"}
        </span>
      </div>
      <div ref={messagesRef} className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-2 lol-scrollbar">
        {messages.length ? messages.map((message) => (
          <div key={message.id || `${message.sender}-${message.content}`} className="text-[11px] leading-snug text-text-secondary">
            <span className="font-bold text-emerald-100/85">{message.sender || "Player"}: </span>
            <span>{message.content}</span>
          </div>
        )) : (
          <div className="flex h-full items-center justify-center text-center text-[10px] uppercase tracking-[0.16em] text-text-faint">
            No messages
          </div>
        )}
      </div>
      <div className="flex gap-1.5 border-t border-white/[0.06] p-2">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") sendMessage();
          }}
          placeholder="Message"
          className="min-w-0 flex-1 rounded-md border border-white/10 bg-black/35 px-2 py-1.5 text-xs text-text-primary outline-none placeholder:text-text-faint focus:border-emerald-300/45"
        />
        <Button type="button" variant="secondary" size="sm" className="px-3" disabled={!connected || !draft.trim()} onClick={sendMessage}>
          Send
        </Button>
      </div>
    </aside>
  );
}
